import React, { useState, useEffect } from 'react';
import { addGoal, getGoal, saveNutritionPlan, getNutritionPlans, addNutritionPDF, getTraineesByTrainerId, getImage } from '../../utils/api';
import Modal from 'react-modal';
import { Box, Button, Typography, List, ListItem, ListItemText, IconButton, TextField, RadioGroup, FormControlLabel, Radio, FormControl, FormLabel, Select, MenuItem, InputLabel } from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { Link } from 'react-router-dom';
import Loader from '../Loader';
import CaloricGoal from './CaloricGoal';
import { GoogleGenerativeAI } from '@google/generative-ai';
import jsPDF from 'jspdf';
import GveretLevinFont from '../../assets/gevlevin-normal.js';
import { PlayArrow as PlayArrowIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

Modal.setAppElement('#root');
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const NutritionPlans = ({ userId }) => {
    const [caloricGoal, setCaloricGoal] = useState(null);
    const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [plans, setPlans] = useState([]);
    const [savedPlans, setSavedPlans] = useState([]);
    const [thumbnails, setThumbnails] = useState([]);
    const [isLoadingThumbnails, setIsLoadingThumbnails] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [scale, setScale] = useState(0.6);
    const [trainees, setTrainees] = useState([]);
    const [isTraineeModalOpen, setIsTraineeModalOpen] = useState(false);
    const [isTraineeModalOpenForGeneration, setIsTraineeModalOpenForGeneration] = useState(false);
    const [isTraineeModalOpenForGoal, setIsTraineeModalOpenForGoal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedTrainee, setSelectedTrainee] = useState(null);
    const [selectedTraineeForGeneration, setSelectedTraineeForGeneration] = useState(null);
    const [pdfFileName, setPdfFileName] = useState('');
    const [userGoal, setUserGoal] = useState('lose');
    const [currentWeight, setCurrentWeight] = useState('');
    const [targetWeight, setTargetWeight] = useState('');
    const [durationMonths, setDurationMonths] = useState('');
    const [isInputModalOpen, setIsInputModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showPopup, setShowPopup] = useState(false);

    // States for caloric goal calculation
    const [isTraineeModalOpenForCalculation, setIsTraineeModalOpenForCalculation] = useState(false);
    const [selectedTraineeForCalculation, setSelectedTraineeForCalculation] = useState(null);
    const [isCalculationModalOpen, setIsCalculationModalOpen] = useState(false);
    const [calculationGoal, setCalculationGoal] = useState('lose');
    const [calculationCurrentWeight, setCalculationCurrentWeight] = useState('');
    const [calculationTargetWeight, setCalculationTargetWeight] = useState('');
    const [calculationDurationMonths, setCalculationDurationMonths] = useState('');
    const [calculationSex, setCalculationSex] = useState('male');
    const [calculationAge, setCalculationAge] = useState('');
    const [calculationHeight, setCalculationHeight] = useState('');
    const [calculationActivityLevel, setCalculationActivityLevel] = useState('sedentary');
    const [calculatedNutrition, setCalculatedNutrition] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [proteinPercentage, setProteinPercentage] = useState(30);
    const [carbsPercentage, setCarbsPercentage] = useState(40);
    const [fatsPercentage, setFatsPercentage] = useState(30);

    const activityLevels = [
        { value: 'sedentary', label: 'יושבני (פעילות מועטה או ללא)', factor: 1.2 },
        { value: 'light', label: 'פעילות קלה (1-3 ימים בשבוע)', factor: 1.375 },
        { value: 'moderate', label: 'פעילות בינונית (3-5 ימים בשבוע)', factor: 1.55 },
        { value: 'active', label: 'פעילות גבוהה (6-7 ימים בשבוע)', factor: 1.725 },
        { value: 'extra', label: 'פעילות אקסטרה (עבודה פיזית קשה או אימונים כפולים)', factor: 1.9 },
    ];

    const role = localStorage.getItem('role');
    const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
    const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });

    const arrayToBase64 = (array) => {
        const uint8Array = new Uint8Array(array);
        const binaryString = uint8Array.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
        return btoa(binaryString);
    };

    useEffect(() => {
        const fetchTraineesData = async () => {
            try {
                const traineesList = await getTraineesByTrainerId(userId);
                const traineesWithImages = await Promise.all(traineesList.map(async (trainee) => {
                    try {
                        const profileImageBase = await getImage(trainee._id);
                        const profileImageBase64 = profileImageBase?.image;
                        return { ...trainee, profileImageBase64 };
                    } catch (error) {
                        console.error(`שגיאה בטעינת תמונה עבור מתאמן ${trainee._id}:`, error);
                        return { ...trainee, profileImageBase64: '/default-avatar.png' };
                    }
                }));
                setTrainees(traineesWithImages);
            } catch (e) {
                console.error('שגיאה בטעינת המתאמנים:', e);
            } finally {
                setIsLoadingThumbnails(false);
            }
        };
        fetchTraineesData();
    }, [userId]);

    useEffect(() => {
        const fetchGoalAndPlans = async () => {
            try {
                const saved = await getNutritionPlans(userId);
                setSavedPlans(saved);
                setIsLoadingThumbnails(true);
                const thumbs = await Promise.all(
                    saved.map(async plan => ({ id: plan._id, thumbnailUrl: await generateThumbnail(plan.fileBlob) }))
                );
                setThumbnails(thumbs.filter(t => t.thumbnailUrl));
            } catch (e) {
                console.error(e);
            } finally {
                setIsLoadingThumbnails(false);
            }
        };
        fetchGoalAndPlans();
    }, [userId]);

    useEffect(() => {
        if (isCalculationModalOpen) {
            setCalculatedNutrition(null);
            setErrorMessage('');
        }
    }, [isCalculationModalOpen]);

    const handleInputSubmit = () => {
        const cw = parseFloat(currentWeight);
        const tw = parseFloat(targetWeight);
        const dm = parseFloat(durationMonths);

        if (!currentWeight || !targetWeight || !durationMonths) {
            setErrorMessage('יש למלא את כל השדות.');
            return;
        }

        if (isNaN(cw) || isNaN(tw) || isNaN(dm)) {
            setErrorMessage('יש להזין מספרים תקינים.');
            return;
        }

        if (dm <= 0) {
            setErrorMessage('מספר החודשים חייב להיות גדול מ-0.');
            return;
        }

        if (userGoal === 'lose' && cw <= tw) {
            setErrorMessage('עבור יעד של ירידה במשקל, המשקל הנוכחי חייב להיות גבוה מהמשקל היעד.');
            return;
        }

        if (userGoal === 'gain' && cw >= tw) {
            setErrorMessage('עבור יעד של עלייה במשקל, המשקל הנוכחי חייב להיות נמוך מהמשקל היעד.');
            return;
        }

        setErrorMessage('');
        setIsInputModalOpen(false);
    };

    const generateNutritionPlans = () => {
        if (role !== 'trainer') return;
        setIsTraineeModalOpenForGeneration(true);
    };

    const handleTraineeSelectForGeneration = async (trainee) => {
        setSelectedTraineeForGeneration(trainee);
        setIsTraineeModalOpenForGeneration(false);
        try {
            const goal = await getGoal(trainee._id);
            if (goal && goal.length) {
                setCaloricGoal(goal[0].calories);
                generatePlansForTrainee(trainee, goal[0].calories);
            } else {
                setIsGoalModalOpen(true);
            }
        } catch (e) {
            console.error('Error fetching goal:', e);
            setIsGoalModalOpen(true);
        }
    };

    const handleTraineeSelectForGoal = (trainee) => {
        setSelectedTrainee(trainee);
        setIsTraineeModalOpenForGoal(false);
        setIsGoalModalOpen(true);
    };

    const generatePlansForTrainee = async (trainee, caloricGoal) => {
        setIsGenerating(true);
        try {
            const prompt = `צור שני תפריטי תזונה יומיים שונים עם צריכה קלורית יומית של ${caloricGoal} קלוריות. כל תפריט צריך לכלול ארוחת בוקר, ארוחת צהריים, ארוחת ערב וחטיפים, עם לפחות שתי אפשרויות לכל ארוחה. לכל אפשרות, ציין את כמות הקלוריות, החלבונים, הפחמימות והשומנים. נא להציג את התפריטים בפורמט הבא בדיוק, ללא שינויים:
            **תפריט יום 1**  
            - **ארוחת בוקר**  
            - אפשרות 1: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - אפשרות 2: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - **ארוחת צהריים**  
            - אפשרות 1: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - אפשרות 2: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - **ארוחת ערב**  
            - אפשרות 1: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - אפשרות 2: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - **חטיפים**  
            - אפשרות 1: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - אפשרות 2: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            **תפריט יום 2**  
            - **ארוחת בוקר**  
            - אפשרות 1: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - אפשרות 2: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - **ארוחת צהריים**  
            - אפשרות 1: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - אפשרות 2: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - **ארוחת ערב**  
            - אפשרות 1: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - אפשרות 2: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - **חטיפים**  
            - אפשרות 1: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            - אפשרות 2: [תיאור המנה] (קלוריות: X, חלבון: Y גרם, פחמימות: Z גרם, שומן: W גרם)  
            נא לוודא שהתפריטים מותאמים ליעד הקלורי ושהמידע מוצג בצורה ברורה ומדויקת, תוך שמירה קפדנית על הפורמט שצוין לעיל.`;
            const result = await model.generateContent(prompt);
            const text = result.response.text();
            const generatedPlans = parseNutritionPlans(text);
            setPlans(generatedPlans);
        } catch (error) {
            console.error('Error generating nutrition plans:', error);
        } finally {
            setIsGenerating(false);
        }
    };

    const parseNutritionPlans = (text) => {
        const lines = text.split('\n').map(line => line.trim());
        const plans = [];
        let currentPlan = null;
        let currentMeal = null;

        const planRegex = /^\*\*תפריט יום (\d+)\*\*$/;
        const mealRegex = /^-\s\*\*(\S+(?: \S+)*)\*\*$/;
        const optionRegex = /^-\sאפשרות (\d+):\s(.+?)\s\(קלוריות: (\d+),\sחלבון: (\d+) גרם,\sפחמימות: (\d+) גרם,\sשומן: (\d+) גרם\)$/;

        for (const line of lines) {
            const planMatch = line.match(planRegex);
            if (planMatch) {
                const day = planMatch[1];
                currentPlan = { planName: `תפריט יום ${day}`, meals: [] };
                plans.push(currentPlan);
                currentMeal = null;
                continue;
            }

            const mealMatch = line.match(mealRegex);
            if (mealMatch && currentPlan) {
                const mealType = mealMatch[1];
                currentMeal = { mealType, options: [] };
                currentPlan.meals.push(currentMeal);
                continue;
            }

            const optionMatch = line.match(optionRegex);
            if (optionMatch && currentMeal) {
                const option = {
                    name: `אפשרות ${optionMatch[1]}`,
                    description: optionMatch[2],
                    calories: parseInt(optionMatch[3]),
                    proteins: parseInt(optionMatch[4]),
                    carbs: parseInt(optionMatch[5]),
                    fats: parseInt(optionMatch[6])
                };
                currentMeal.options.push(option);
            }
        }

        return plans;
    };

    const getBlobFromFileBlob = fileBlob => {
        if (fileBlob instanceof Blob) return fileBlob;
        if (typeof fileBlob === 'string') {
            const byteChars = atob(fileBlob);
            const arr = [];
            for (let i = 0; i < byteChars.length; i += 1024) {
                const slice = byteChars.slice(i, i + 1024);
                const bytes = new Uint8Array([...slice].map(c => c.charCodeAt(0)));
                arr.push(bytes);
            }
            return new Blob(arr, { type: 'application/pdf' });
        }
        if (fileBlob?.type === 'Buffer') {
            const bytes = new Uint8Array(fileBlob.data);
            return new Blob([bytes], { type: 'application/pdf' });
        }
        return null;
    };

    const generateThumbnail = async fileBlob => {
        const blob = getBlobFromFileBlob(fileBlob);
        if (!blob) return null;
        const url = URL.createObjectURL(blob);
        try {
            const doc = await pdfjsLib.getDocument(url).promise;
            const page = await doc.getPage(1);
            const viewport = page.getViewport({ scale: 0.5 });
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await page.render({ canvasContext: ctx, viewport }).promise;
            return canvas.toDataURL();
        } catch (e) {
            console.error(e);
            return null;
        } finally {
            URL.revokeObjectURL(url);
        }
    };

    const openModal = (fileBlob, fileName) => {
        const blob = getBlobFromFileBlob(fileBlob);
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setPdfFileName(fileName);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
        setPdfDoc(null);
        setPdfFileName('');
    };

    const handleDownload = () => {
        if (!pdfUrl) return;
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = pdfFileName || 'nutrition_plan.pdf';
        link.click();
    };

    const renderPDF = async doc => {
        const container = document.getElementById('pdf-container');
        if (!container) return;
        container.innerHTML = '';
        for (let i = 1; i <= doc.numPages; i++) {
            const page = await doc.getPage(i);
            const viewport = page.getViewport({ scale });
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await page.render({ canvasContext: ctx, viewport }).promise;
            container.appendChild(canvas);
        }
    };

    useEffect(() => {
        if (pdfUrl && isModalOpen) {
            pdfjsLib.getDocument(pdfUrl).promise.then(doc => {
                setPdfDoc(doc);
                renderPDF(doc);
            }).catch(console.error);
        }
    }, [pdfUrl, isModalOpen]);

    useEffect(() => {
        if (pdfDoc) renderPDF(pdfDoc);
    }, [scale]);

    const handleTraineeSelect = (trainee) => {
        setSelectedTrainee(trainee);
        setIsConfirmModalOpen(true);
    };

    const handleConfirmSave = () => {
        handleSavePlan(selectedPlan, selectedTrainee);
        setIsConfirmModalOpen(false);
        setIsTraineeModalOpen(false);
    };

    const handleSavePlan = async (plan, trainee) => {
        try {
            const transformedPlan = {
                userId: trainee._id,
                planName: plan.planName,
                meals: plan.meals.map(meal => ({
                    mealType: { 'ארוחת בוקר': 'breakfast', 'ארוחת צהריים': 'lunch', 'ארוחת ערב': 'dinner', 'חטיפים': 'snack' }[meal.mealType] || meal.mealType,
                    options: meal.options.map(option => ({
                        name: option.name,
                        description: option.description,
                        calories: option.calories,
                        proteins: option.proteins,
                        carbs: option.carbs,
                        fats: option.fats
                    }))
                }))
            };

            const savedPlan = await saveNutritionPlan(trainee._id, transformedPlan);
            const nutritionPlanId = savedPlan.plan._id;
            const pdfBlob = generatePDF(plan, trainee);
            const formData = new FormData();
            formData.append('userId', trainee._id);
            formData.append('nutritionPlanId', nutritionPlanId);
            formData.append('programFile', pdfBlob, `${plan.planName}.pdf`);
            await addNutritionPDF(formData);
            setShowPopup(true);
        } catch (error) {
            console.error('Error saving plan:', error);
        }
    };

    const generatePDF = (planData, trainee) => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            textDirection: 'rtl',
            direction: 'rtl',
        });
        doc.setR2L(true);
        doc.addFileToVFS('gevlevin.ttf', GveretLevinFont);
        doc.addFont('gevlevin.ttf', 'GveretLevin', 'normal');
        doc.setFont('GveretLevin');
        doc.setLanguage('he');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);

        const pageWidth = 210;
        const rightMargin = 10;
        const leftMargin = 10;
        const textX = pageWidth - rightMargin;
        const indent = 5;

        const addWrappedText = (text, x, y, maxWidth) => {
            const lines = doc.splitTextToSize(text, maxWidth);
            doc.text(lines, x, y, { align: 'right' });
            const lineHeight = 6;
            return lines.length * lineHeight;
        };

        doc.text(`תוכנית תזונה עבור ${trainee.username}`, 105, 10, { align: 'center' });
        let y = 20;

        y += addWrappedText(planData.planName, textX, y, textX - leftMargin);
        y += addWrappedText(`יעד קלורי יומי: ${caloricGoal} קלוריות`, textX, y, textX - leftMargin);
        y += 10;

        planData.meals.forEach(meal => {
            y += addWrappedText(meal.mealType, textX, y, textX - leftMargin);
            meal.options.forEach((option, index) => {
                const optionText = `${index + 1}. ${option.description}: ${option.calories} קלוריות, ${option.proteins}ג חלבון, ${option.carbs}ג פחמימות, ${option.fats}ג שומן`;
                const indentedX = textX - indent;
                y += addWrappedText(optionText, indentedX, y, indentedX - leftMargin);
            });
            y += 5;
        });

        return doc.output('blob');
    };

    const handleCalculationSubmit = async () => {
        setIsCalculating(true);
        const cw = parseFloat(calculationCurrentWeight);
        const tw = parseFloat(calculationTargetWeight);
        const dm = parseFloat(calculationDurationMonths);
        const age = parseFloat(calculationAge);
        const height = parseFloat(calculationHeight);
        const proteinPct = parseFloat(proteinPercentage);
        const carbsPct = parseFloat(carbsPercentage);
        const fatsPct = parseFloat(fatsPercentage);

        if (!calculationGoal || !cw || !tw || !dm || !calculationSex || !age || !height || !calculationActivityLevel ||
            isNaN(proteinPct) || isNaN(carbsPct) || isNaN(fatsPct)) {
            setErrorMessage('יש למלא את כל השדות.');
            setIsCalculating(false);
            return;
        }

        if (dm <= 0) {
            setErrorMessage('מספר החודשים חייב להיות גדול מ-0.');
            setIsCalculating(false);
            return;
        }

        if (calculationGoal === 'lose' && cw <= tw) {
            setErrorMessage('עבור יעד של ירידה במשקל, המשקל הנוכחי חייב להיות גבוה מהמשקל היעד.');
            setIsCalculating(false);
            return;
        }

        if (calculationGoal === 'gain' && cw >= tw) {
            setErrorMessage('עבור יעד של עלייה במשקל, המשקל הנוכחי חייב להיות נמוך מהמשקל היעד.');
            setIsCalculating(false);
            return;
        }

        if (proteinPct + carbsPct + fatsPct !== 100) {
            setErrorMessage('סך האחוזים של המקרו-נוטריאנטים חייב להיות 100.');
            setIsCalculating(false);
            return;
        }

        // Simulate async calculation with a delay (replace with actual async logic if needed)
        await new Promise(resolve => setTimeout(resolve, 1000));

        let bmr;
        if (calculationSex === 'male') {
            bmr = 10 * cw + 6.25 * height - 5 * age + 5;
        } else {
            bmr = 10 * cw + 6.25 * height - 5 * age - 161;
        }

        const activityFactor = activityLevels.find(level => level.value === calculationActivityLevel).factor;
        const maintenanceCalories = bmr * activityFactor;
        const weightChange = Math.abs(tw - cw);
        const totalCaloriesNeeded = weightChange * 7700;
        const numberOfDays = dm * 30;
        const dailyAdjustment = totalCaloriesNeeded / numberOfDays;
        let dailyCaloricGoal = calculationGoal === 'lose'
            ? maintenanceCalories - dailyAdjustment
            : maintenanceCalories + dailyAdjustment;

        dailyCaloricGoal = Math.round(dailyCaloricGoal / 10) * 10;

        const proteins = Math.round((dailyCaloricGoal * (proteinPct / 100)) / 4);
        const carbs = Math.round((dailyCaloricGoal * (carbsPct / 100)) / 4);
        const fats = Math.round((dailyCaloricGoal * (fatsPct / 100)) / 9);

        setCalculatedNutrition({
            calories: dailyCaloricGoal,
            proteins,
            carbs,
            fats
        });
        setCaloricGoal(dailyCaloricGoal);
        setErrorMessage('');
        setIsCalculating(false);
    };

    const handleSaveCalculatedGoal = async () => {
        if (selectedTraineeForCalculation && calculatedNutrition) {
            console.log(`Setting nutrition goal for ${selectedTraineeForCalculation.username}: Calories: ${calculatedNutrition.calories}, Proteins: ${calculatedNutrition.proteins}g, Carbs: ${calculatedNutrition.carbs}g, Fats: ${calculatedNutrition.fats}g`);
            await addGoal(selectedTraineeForCalculation._id, calculatedNutrition);
            // Replace with actual API call: setNutritionGoal(selectedTraineeForCalculation._id, calculatedNutrition);
            setIsCalculationModalOpen(false);
        }
    };

    return (
        <Box sx={{ padding: '20px', direction: 'rtl', textAlign: 'center' }}>
            <Modal
                isOpen={isInputModalOpen}
                onRequestClose={() => setIsInputModalOpen(false)}
                style={{
                    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
                    content: {
                        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
                        transform: 'translate(-50%, -50%)', width: '400px', maxWidth: '90vw',
                        padding: '20px', borderRadius: '8px', backgroundColor: '#fff',
                        height: 'auto', direction: 'rtl', textAlign: 'right'
                    },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography fontFamily="GveretLevin" direction="rtl" textAlign="right" variant="h6">מה המטרה שלך?</Typography>
                    <FormControl direction="rtl" textAlign="right">
                        <FormLabel direction="rtl" textAlign="right" id="goal-label">בחר יעד</FormLabel>
                        <RadioGroup
                            direction="rtl"
                            textAlign="right"
                            aria-labelledby="goal-label"
                            value={userGoal}
                            onChange={e => setUserGoal(e.target.value)}
                        >
                            <FormControlLabel value="lose" control={<Radio />} label="לרדת במשקל" />
                            <FormControlLabel value="gain" control={<Radio />} label="להעלות במשקל" />
                        </RadioGroup>
                    </FormControl>
                    <TextField
                        direction="rtl"
                        textAlign="right"
                        label="משקל נוכחי (קג)"
                        value={currentWeight}
                        onChange={e => setCurrentWeight(e.target.value)}
                        type="number"
                    />
                    <TextField
                        direction="rtl"
                        textAlign="right"
                        label="משקל יעד (קג)"
                        value={targetWeight}
                        onChange={e => setTargetWeight(e.target.value)}
                        type="number"
                    />
                    <TextField
                        direction="rtl"
                        textAlign="right"
                        label="מספר חודשים"
                        value={durationMonths}
                        onChange={e => setDurationMonths(e.target.value)}
                        type="number"
                    />
                    {errorMessage && <Typography color="error">{errorMessage}</Typography>}
                    <Button
                        sx={{ backgroundColor: "#B5A78A", color: "white", "&:hover": { backgroundColor: "#B5A78A" } }}
                        variant="contained"
                        onClick={handleInputSubmit}
                        disabled={!currentWeight || !targetWeight || !durationMonths}
                    >אישור</Button>
                </Box>
            </Modal>

            <Modal
                isOpen={isTraineeModalOpen}
                onRequestClose={() => setIsTraineeModalOpen(false)}
                style={{
                    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
                    content: {
                        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
                        transform: 'translate(-50%, -50%)', width: '600px', maxWidth: '90vw',
                        padding: '20px', borderRadius: '8px', backgroundColor: '#fff',
                        direction: 'rtl', textAlign: 'right'
                    },
                }}
            >
                <Typography variant="h6">בחר מתאמן לשמירת התוכנית</Typography>
                {trainees.length === 0 ? (
                    <Typography>אין מתאמנים זמינים.</Typography>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2, maxHeight: '400px', overflowY: 'auto' }}>
                        {trainees.map(trainee => (
                            <Box key={trainee._id} onClick={() => handleTraineeSelect(trainee)} sx={{ cursor: 'pointer', textAlign: 'center' }}>
                                <img src={trainee.profileImageBase64} alt={trainee.username} style={{ width: '100px', height: '100px', borderRadius: '50%' }} />
                                <Typography>{trainee.username}</Typography>
                                <Typography>{trainee.phone}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Modal>

            <Modal
                isOpen={isTraineeModalOpenForGeneration}
                onRequestClose={() => setIsTraineeModalOpenForGeneration(false)}
                style={{
                    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
                    content: {
                        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
                        transform: 'translate(-50%, -50%)', width: '600px', maxWidth: '90vw',
                        padding: '20px', borderRadius: '8px', backgroundColor: '#fff',
                        direction: 'rtl', textAlign: 'right'
                    },
                }}
            >
                <Typography variant="h6">בחר מתאמן ליצירת תוכנית תזונה</Typography>
                {trainees.length === 0 ? (
                    <Typography>אין מתאמנים זמינים.</Typography>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2 }}>
                        {trainees.map(trainee => (
                            <Box key={trainee._id} onClick={() => handleTraineeSelectForGeneration(trainee)} sx={{ cursor: 'pointer', textAlign: 'center' }}>
                                <img src={trainee.profileImageBase64} alt={trainee.username} style={{ width: '100px', height: '100px', borderRadius: '50%' }} />
                                <Typography>{trainee.username}</Typography>
                                <Typography>{trainee.phone}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Modal>

            <Modal
                isOpen={isTraineeModalOpenForGoal}
                onRequestClose={() => setIsTraineeModalOpenForGoal(false)}
                style={{
                    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
                    content: {
                        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
                        transform: 'translate(-50%, -50%)', width: '600px', maxWidth: '90vw',
                        padding: '20px', borderRadius: '8px', backgroundColor: '#fff',
                        direction: 'rtl', textAlign: 'right'
                    },
                }}
            >
                <Typography variant="h6">בחר מתאמן להגדרת יעד קלורי</Typography>
                {trainees.length === 0 ? (
                    <Typography>אין מתאמנים זמינים.</Typography>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2 }}>
                        {trainees.map(trainee => (
                            <Box key={trainee._id} onClick={() => handleTraineeSelectForGoal(trainee)} sx={{ cursor: 'pointer', textAlign: 'center' }}>
                                <img src={trainee.profileImageBase64} alt={trainee.username} style={{ width: '100px', height: '100px', borderRadius: '50%' }} />
                                <Typography>{trainee.username}</Typography>
                                <Typography>{trainee.phone}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Modal>

            <Modal
                isOpen={isConfirmModalOpen}
                onRequestClose={() => setIsConfirmModalOpen(false)}
                style={{
                    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
                    content: {
                        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
                        transform: 'translate(-50%, -50%)', width: '400px', maxWidth: '90vw',
                        padding: '20px', borderRadius: '8px', backgroundColor: '#fff',
                        direction: 'rtl', textAlign: 'right'
                    },
                }}
            >
                <Typography fontFamily="GveretLevin" color="#B5A78A" variant="h6">אישור שמירה</Typography>
                <Typography>האם אתה בטוח שברצונך להוסיף את תוכנית התזונה ל{selectedTrainee?.username}?</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button onClick={handleConfirmSave}>כן</Button>
                    <Button onClick={() => setIsConfirmModalOpen(false)}>לא</Button>
                </Box>
            </Modal>

            <Modal
                isOpen={isTraineeModalOpenForCalculation}
                onRequestClose={() => setIsTraineeModalOpenForCalculation(false)}
                style={{
                    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
                    content: {
                        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
                        transform: 'translate(-50%, -50%)', width: '600px', maxWidth: '90vw',
                        padding: '20px', borderRadius: '8px', backgroundColor: '#fff',
                        direction: 'rtl', textAlign: 'right'
                    },
                }}
            >
                <Typography variant="h6">בחר מתאמן לחישוב יעד קלורי</Typography>
                {trainees.length === 0 ? (
                    <Typography>אין מתאמנים זמינים.</Typography>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2, maxHeight: '400px', overflowY: 'auto' }}>
                        {trainees.map(trainee => (
                            <Box key={trainee._id} onClick={() => {
                                setSelectedTraineeForCalculation(trainee);
                                setIsTraineeModalOpenForCalculation(false);
                                setIsCalculationModalOpen(true);
                            }} sx={{ cursor: 'pointer', textAlign: 'center' }}>
                                <img src={trainee.profileImageBase64} alt={trainee.username} style={{ width: '100px', height: '100px', borderRadius: '50%' }} />
                                <Typography>{trainee.username}</Typography>
                                <Typography>{trainee.phone}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Modal>

            <Modal
                isOpen={isCalculationModalOpen}
                onRequestClose={() => setIsCalculationModalOpen(false)}
                style={{
                    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
                    content: {
                        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
                        transform: 'translate(-50%, -50%)', width: '600px', maxWidth: '90vw',
                        padding: '20px', borderRadius: '8px', backgroundColor: '#fff',
                        direction: 'rtl', textAlign: 'right',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    },
                }}
            >
                <Typography variant="h6">חישוב יעד קלורי עבור {selectedTraineeForCalculation?.username}</Typography>
                <Box component="form" sx={{ mt: 2 }}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">מטרה</FormLabel>
                        <RadioGroup
                            row
                            value={calculationGoal}
                            onChange={e => setCalculationGoal(e.target.value)}
                        >
                            <FormControlLabel value="lose" control={<Radio />} label="לרדת במשקל" />
                            <FormControlLabel value="gain" control={<Radio />} label="להעלות במשקל" />
                        </RadioGroup>
                    </FormControl>
                    <TextField
                        label="משקל נוכחי (קג)"
                        type="number"
                        value={calculationCurrentWeight}
                        onChange={e => setCalculationCurrentWeight(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="משקל יעד (קג)"
                        type="number"
                        value={calculationTargetWeight}
                        onChange={e => setCalculationTargetWeight(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="מספר חודשים"
                        type="number"
                        value={calculationDurationMonths}
                        onChange={e => setCalculationDurationMonths(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <FormControl component="fieldset" sx={{ mt: 2 }}>
                        <FormLabel component="legend">מין</FormLabel>
                        <RadioGroup
                            row
                            value={calculationSex}
                            onChange={e => setCalculationSex(e.target.value)}
                        >
                            <FormControlLabel value="male" control={<Radio />} label="זכר" />
                            <FormControlLabel value="female" control={<Radio />} label="נקבה" />
                        </RadioGroup>
                    </FormControl>
                    <TextField
                        label="גיל (שנים)"
                        type="number"
                        value={calculationAge}
                        onChange={e => setCalculationAge(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="גובה (סמ)"
                        type="number"
                        value={calculationHeight}
                        onChange={e => setCalculationHeight(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>רמת פעילות</InputLabel>
                        <Select
                            value={calculationActivityLevel}
                            onChange={e => setCalculationActivityLevel(e.target.value)}
                        >
                            {activityLevels.map(level => (
                                <MenuItem key={level.value} value={level.value}>
                                    {level.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="אחוז חלבון"
                        type="number"
                        value={proteinPercentage}
                        onChange={e => setProteinPercentage(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="אחוז פחמימות"
                        type="number"
                        value={carbsPercentage}
                        onChange={e => setCarbsPercentage(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="אחוז שומן"
                        type="number"
                        value={fatsPercentage}
                        onChange={e => setFatsPercentage(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <Typography variant="body2" color="textSecondary">סך האחוזים חייב להיות 100.</Typography>
                    {errorMessage && <Typography color="error">{errorMessage}</Typography>}
                    <Button
                        variant="contained"
                        onClick={handleCalculationSubmit}
                        disabled={isCalculating}
                        sx={{ mt: 2 }}
                    >
                        {isCalculating ? <Loader /> : 'חשב'}
                    </Button>
                </Box>
                {calculatedNutrition && (
                    <Box sx={{ mt: 2, p: 2, border: '1px solid #ccc', borderRadius: '4px' }}>
                        <Typography>היעד הקלורי היומי המומלץ הוא {calculatedNutrition.calories} קלוריות.</Typography>
                        <Typography>חלבון: {calculatedNutrition.proteins} גרם</Typography>
                        <Typography>פחמימות: {calculatedNutrition.carbs} גרם</Typography>
                        <Typography>שומן: {calculatedNutrition.fats} גרם</Typography>
                        <Button onClick={handleSaveCalculatedGoal} sx={{ mt: 1 }}>שמור יעד</Button>
                    </Box>
                )}
            </Modal>

            <Typography fontFamily="GveretLevin" variant="h4">תפריט תזונה מותאם</Typography>

            {role === 'trainer' && (
                <Button onClick={() => setIsTraineeModalOpenForCalculation(true)}>
                    1.יצירה ועדכון יעד למתאמן
                    </Button>
            )}

            {role === 'trainer' && (
                <Button onClick={() => setIsTraineeModalOpenForGoal(true)}>
                    2. בדיקת יעד קלורי
                </Button>
            )}


            {role === 'trainer' && (
                <Button onClick={generateNutritionPlans} disabled={isGenerating}>
                    {isGenerating ? <Loader /> : '3. צור תפריט תזונה למתאמן'}
                </Button>
            )}
            {plans.map((plan, index) => (
                <Box key={index} sx={{ margin: '20px 0', direction: 'rtl', textAlign: 'right' }}>
                    <Typography variant="h6">{plan.planName}</Typography>
                    {plan.meals.map((meal, mealIndex) => (
                        <Box key={mealIndex} sx={{ margin: '10px 0', direction: 'rtl', textAlign: 'right' }}>
                            <Typography direction="rtl" textAlign="right" fontFamily="GveretLevin" variant="subtitle1">{meal.mealType}</Typography>
                            <List sx={{ direction: 'rtl', textAlign: 'right' }}>
                                {meal.options.map((option, optionIndex) => (
                                    <ListItem sx={{ direction: 'rtl', textAlign: 'right' }} key={optionIndex}>
                                        <ListItemText
                                            primary={option.description}
                                            secondary={`קלוריות: ${option.calories}, חלבון: ${option.proteins} גרם, פחמימות: ${option.carbs} גרם, שומן: ${option.fats} גרם`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    ))}
                    {role === 'trainer' && selectedTraineeForGeneration && (
                        <Button onClick={() => { setSelectedPlan(plan); handleSavePlan(plan, selectedTraineeForGeneration); }}>
                            שמירת תכנית
                        </Button>
                    )}
                </Box>
            ))}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                {savedPlans.map(plan => {
                    const thumb = thumbnails.find(t => t.id === plan._id);
                    return (
                        <Box key={plan._id} sx={{
                            position: 'relative', cursor: 'pointer', textAlign: "center",
                            padding: "10px", backgroundColor: "#fff", borderRadius: "5px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)", cursor: "pointer"
                        }} onClick={() => openModal(plan.fileBlob, plan.fileName)}>
                            {isLoadingThumbnails ? (
                                <Box sx={{ width: '100%', height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader /></Box>
                            ) : thumb?.thumbnailUrl ? (
                                <img src={thumb.thumbnailUrl} alt={plan.fileName} style={{ width: '100%' }} />
                            ) : (
                                <Box sx={{ width: '100%', height: 150, bgcolor: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography>Thumbnail not available</Typography>
                                </Box>
                            )}
                            <Typography noWrap sx={{ mt: 1 }}>{plan.fileName}</Typography>
                            <Box sx={{ position: 'flex-end', top: 8, right: 8 }}>
                                <IconButton
                                    component={Link}
                                    to={plan.nutritionPlanId && plan.nutritionPlanId._id ? `/start-nutrition/${plan.nutritionPlanId._id}` : '#'}
                                    onClick={e => {
                                        e.stopPropagation();
                                        if (!plan.nutritionPlanId || !plan.nutritionPlanId._id) {
                                            console.error('Invalid plan ID:', plan);
                                        }
                                    }}
                                    disabled={!plan.nutritionPlanId || !plan.nutritionPlanId._id}
                                >
                                    <PlayArrowIcon />  תחילת המעקב
                                </IconButton>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                style={{
                    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' },
                    content: {
                        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                        backgroundColor: '#fff', border: 'none', borderRadius: '8px', width: '80%', height: '80%',
                        maxWidth: '1200px', maxHeight: '90vh', padding: '0', margin: '0', display: 'flex',
                        justifyContent: 'center', alignItems: 'center', overflow: 'auto',
                    },
                }}
            >
                <button
                    onClick={closeModal}
                    style={{ position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', color: '#000', fontSize: '30px', cursor: 'pointer' }}
                >
                    ×
                </button>
                <IconButton
                    onClick={handleDownload}
                    style={{ position: 'absolute', top: '10px', left: '10px', zIndex: 2 }}
                >
                    <FileDownloadIcon />
                </IconButton>
                <div
                    style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', zIndex: 1, display: 'flex', gap: '10px' }}
                >
                    <span onClick={() => setScale(prev => Math.min(prev + 0.1, 3))} style={{ fontSize: '24px', cursor: 'pointer', userSelect: 'none' }}>+</span>
                    <span onClick={() => setScale(prev => Math.max(prev - 0.1, 0.5))} style={{ fontSize: '24px', cursor: 'pointer', userSelect: 'none' }}>-</span>
                </div>
                <div id="pdf-container" style={{ width: '100%', height: '100%', overflow: 'auto' }}></div>
            </Modal>

            {showPopup && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                    onClick={() => setShowPopup(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#FFF',
                            padding: '40px',
                            borderRadius: '10px',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            maxWidth: '400px',
                            width: '70%'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <span style={{ fontSize: '48px' }}>👍</span>
                        </div>
                        <p style={{ fontSize: '24px', fontWeight: '700', textAlign: 'center', marginBottom: '20px' }}>
                            התוכנית נשמרה!
                        </p>
                        <button
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#B5A78A',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onClick={() => setShowPopup(false)}
                        >
                            <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#FFD700', marginRight: '5px' }}>סגירה</span>
                        </button>
                    </div>
                </div>
            )}

            <Modal isOpen={isGoalModalOpen} onRequestClose={() => setIsGoalModalOpen(false)}>
                <CaloricGoal
                    userId={selectedTraineeForGeneration?._id || selectedTrainee?._id}
                    onUpdateGoal={(goal) => {
                        setCaloricGoal(goal.calories);
                        if (selectedTraineeForGeneration) generatePlansForTrainee(selectedTraineeForGeneration, goal.calories);
                        setIsGoalModalOpen(false);
                    }}
                />
            </Modal>
        </Box>
    );
};

export default NutritionPlans;